// recursively traverse any objects to detect changed fields, this is useful in cases
// where we are updating multiple objects / fields at once
const isSameValue = (previous: unknown, next: unknown): boolean => {
  if (previous === next) return true;
  if (typeof previous !== 'object' || typeof next !== 'object' || previous === null || next === null) return false;

  const previousEntries = Object.entries(previous as Record<string, unknown>);
  const nextRecord = next as Record<string, unknown>;
  if (previousEntries.length !== Object.keys(nextRecord).length) return false;

  return previousEntries.every(([key, value]) => isSameValue(value, nextRecord[key]));
};

// Walks down to the leaf paths that actually differ, so tracking reflects the real
// changed value (e.g. `pickupServices.isLiftgatePickup`) instead of the top-level field.
const walkChangedFieldPaths = (previous: unknown, next: unknown, path: string): string[] => {
  if (isSameValue(previous, next)) return [];

  const isPreviousObject = typeof previous === 'object' && previous !== null;
  const isNextObject = typeof next === 'object' && next !== null;
  if (!isPreviousObject || !isNextObject) return [path];

  const previousRecord = previous as Record<string, unknown>;
  const nextRecord = next as Record<string, unknown>;
  const keys = new Set([...Object.keys(previousRecord), ...Object.keys(nextRecord)]);

  return Array.from(keys).flatMap((key) => walkChangedFieldPaths(previousRecord[key], nextRecord[key], `${path}.${key}`));
};

/**
 * Diffs two same-shaped objects and returns the full dot-joined leaf paths that changed
 * (e.g. `handlingUnits.0.length`), rather than just the top-level field name. A single
 * `next` can replace several fields at once (e.g. hydrating saved defaults into a form),
 * so this returns all of them rather than just the first.
 */
export const getChangedFieldPaths = <T extends Record<string, unknown>>(previous: T, next: T): string[] =>
  (Object.keys(next) as (keyof T)[]).flatMap((field) => walkChangedFieldPaths(previous[field], next[field], field as string));
